// TODO - protect this route from direct access

import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { Store } from '@ngrx/store';
// date-fns
import { format } from 'date-fns';
import { get as _get, some as _some } from 'lodash';
import { combineLatest, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

// Store
import {
  claimFnolQuery,
  CreateDraftClaimClass,
  CreateDraftClaimData,
  DraftClaim,
  fromClaimFnolActions,
  ReportClaimFnolService
} from '@amfam/claim/fnol/data-access';
import { ClaimUtilService } from '@amfam/claim/shared/utility';
import { PolicySelectors } from '@amfam/policy/data-access';
import { AnalyticsFacade, ClaimsAnalytics } from '@amfam/shared/analytics';
// Constants file for claims
import { ClaimsNavigationPaths } from '@amfam/shared/models';
import { userQuery } from '@amfam/shared/user';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { ErrorObj, LoadingSpinnerService, ValidationService } from '@amfam/ui-kit';

import * as fromRoot from '../../../core/store';
import { ClaimsFnolFormControls } from '../../shared/claims-constants';

@Component({
  selector: 'ds-report-claim-incident-time',
  templateUrl: './report-claim-incident-time.component.html',
  styleUrls: ['../report-claim-fnol.component.scss', './report-claim-incident-time.component.scss'],
  standalone: false
})
export class ReportClaimIncidentTimeComponent implements OnInit, OnDestroy {
  public reportIncidentTimeForm: UntypedFormGroup;
  public details: AbstractControl;
  public dateOfIncident: UntypedFormControl;
  public detailsGroup: UntypedFormGroup;

  public pageTitleName = 'File a Claim';

  public states: Array<string> = [];
  public draftClaim: DraftClaim;
  public errorList: Array<ErrorObj> = [];
  private claimNumber: string;
  private draftClaimList: any;
  // Shared Subject for completing all observables on destroy
  private stop$: Subject<void> = new Subject<void>();

  constructor(
    private formBuilder: UntypedFormBuilder,
    private reportClaimFnolService: ReportClaimFnolService,
    private store: Store<fromRoot.RootState>,
    private analyticsFacade: AnalyticsFacade,
    private claimUtilService: ClaimUtilService,
    private spinner: LoadingSpinnerService
  ) {}

  ngOnInit() {
    this.buildForm();

    // Fetch saved draft claim data from report-claim-risk-selection.comp.
    combineLatest(
      this.store.select(claimFnolQuery.getClaimFnolSelectedEntity),
      this.store.select(PolicySelectors.selectPoliciesLoading),
      this.store.select(claimFnolQuery.getClaimFnolEntities),
      this.store.select(PolicySelectors.selectPolicies),
      (draftClaim, policiesLoading, fnolsList, policies) => {
        return {
          draftClaim: draftClaim,
          loading: policiesLoading,
          fnols: fnolsList,
          policies: policies
        };
      }
    )
      .pipe(
        takeUntil(this.stop$),
        filter(state => {
          return state && !_some([state.draftClaim], { loading: true }) && !state.loading;
        })
      )
      .subscribe(state => {
        this.spinner.stop();
        this.draftClaim = state.draftClaim;
        this.claimNumber = this.draftClaim.claimNumber;
        this.draftClaimList = state.fnols;
        if (this.reportIncidentTimeForm.valid) {
          /**
           * AS: If we have gotten a response from the api call after save & continue
           * we need to decide if we redirect to next screen or take the user to summary page.
           */
          if (
            _get(this.draftClaim, 'fnolDetail.typeOfLossLocationCode', undefined) &&
            _get(this.draftClaim, 'claimNumber', undefined)
          ) {
            /**
             * This mean we already have the information for the next screen, take the user to
             * the summary page then.
             */
            this.reportClaimFnolService.getRoutingPath(this.draftClaim);
          } else {
            this.store.dispatch(
              fromRouterActions.Go({
                path: [ClaimsNavigationPaths.CLAIM_INCIDENT_LOCATION_PATH]
              })
            );
          }
        }
        if (state.policies) {
          this.reportClaimFnolService.setClaimsPageTitle(
            state.policies.filter(policy => policy.policyNumber === this.draftClaim.policyNumber)[0]
          );
        }
      });

    /**
     * Condition to check required data is available to populate on landing from edit-risk-summary or via back button.
     */
    if (this.draftClaim && !!this.draftClaim.lossDate) {
      this.getSavedClaimInfo();
    }

    const details = <UntypedFormGroup>this.reportIncidentTimeForm.controls['details'];

    this.analyticsFacade.trackPage(ClaimsAnalytics.pageAutoClaimIncidentInfo);
  }

  // onDestroy emit value and complete
  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }

  buildForm() {
    const d: Date = new Date();
    this.reportIncidentTimeForm = this.formBuilder.group(
      {
        details: this.formBuilder.group({
          dateOfIncident: [
            {
              date: {
                year: d.getFullYear(),
                month: d.getMonth() + 1,
                day: d.getDate()
              },
              formatted: format(new Date(), 'MM/DD/YYYY')
            },
            Validators.required
          ],
          timeOfIncident: [
            '',
            Validators.compose([Validators.required, ValidationService.timeValidator])
          ],
          amPmSelection: ['', Validators.compose([Validators.required])]
        })
      },
      {
        validator: ValidationService.futureTimeValidator(
          'details',
          'timeOfIncident',
          'amPmSelection',
          'dateOfIncident'
        )
      }
    );

    this.detailsGroup = <UntypedFormGroup>this.reportIncidentTimeForm.controls['details'];
  }

  goBackOneStep() {
    this.store.dispatch(
      fromRouterActions.Go({
        path: [ClaimsNavigationPaths.CLAIM_REPORTFNOL_HOME_PATH]
      })
    );
  }

  cancel() {
    this.store.dispatch(
      fromRouterActions.Go({
        path: [ClaimsNavigationPaths.CLAIM_HOME_PATH]
      })
    );
  }

  private getSavedClaimInfo(): void {
    const time: string = this.claimUtilService.convertTime24To12(this.draftClaim.lossDate);
    for (const control in this.detailsGroup.controls) {
      if (control === ClaimsFnolFormControls.DATE_OF_INCIDENT) {
        // Referred from billing module.
        const selectedDateOfLoss = this.draftClaim['lossDate'];
        const d: Date = new Date(format(selectedDateOfLoss, 'MM/DD/YYYY'));
        const selectedDateOfLossObject = {
          date: { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() },
          jsdate: d.toISOString(),
          formatted: format(new Date(selectedDateOfLoss), 'MM/DD/YYYY')
        };
        this.detailsGroup.controls[ClaimsFnolFormControls.DATE_OF_INCIDENT].setValue(
          selectedDateOfLossObject
        );
      } else if (control === ClaimsFnolFormControls.TIME_OF_INCIDENT) {
        this.detailsGroup.controls[ClaimsFnolFormControls.TIME_OF_INCIDENT].setValue(
          time.split(' ')[0]
        );
      } else if (control === ClaimsFnolFormControls.AM_PM_SELECTION) {
        this.detailsGroup.controls[ClaimsFnolFormControls.AM_PM_SELECTION].setValue(
          time.split(' ')[1]
        );
      }
    }
  }

  submitClaimIncident() {
    // Create payload.
    const payLoadDate: Date = new Date(
      this.detailsGroup.controls[ClaimsFnolFormControls.DATE_OF_INCIDENT].value.formatted
    );
    const inputHrsMinutes =
      this.detailsGroup.controls[ClaimsFnolFormControls.TIME_OF_INCIDENT].value;
    const meridian = this.detailsGroup.controls[ClaimsFnolFormControls.AM_PM_SELECTION].value;
    payLoadDate.setHours(
      Number(this.claimUtilService.convertTime12To24(inputHrsMinutes.split(':')[0], meridian))
    );
    payLoadDate.setMinutes(Number(inputHrsMinutes.split(':')[1]));
    /**
     * AS: Party ID and Policy Num are part of the request parameter for making a draft claim request.
     * Sindce these are part of the store we fetch it before adding it to the object being sent.
     */
    let partyId: string;
    this.store.select(userQuery.selectUserState).subscribe(res => {
      partyId = res.customerId;
    });

    const draftClaimPayLoad: CreateDraftClaimData = CreateDraftClaimClass.create(
      format(new Date(payLoadDate), 'YYYY-MM-DDTHH:mm:ss'),
      partyId,
      this.draftClaim.policyNumber,
      this.draftClaim.riskId
    );
    const draftClaim: DraftClaim = Object.assign({}, this.draftClaim, draftClaimPayLoad);
    let claim: DraftClaim;
    let isDuplicate = false;
    /**
     * AS: If the user came in via back button and did not change anything on the date and time page, we do not
     * want to show the 'You have a existing claim' page. Instead we redirect them to the page where they came from.
     */
    if (
      this.draftClaim &&
      this.draftClaim.claimNumber &&
      draftClaim.lossDate === this.draftClaim.lossDate
    ) {
      this.store.dispatch(
        fromRouterActions.Go({
          path: [
            ClaimsNavigationPaths.CLAIM_HOME_PATH +
              '/report-claim-fnol/' +
              this.draftClaim.claimNumber
          ]
        })
      );
    } else {
      for (claim of Array.from(Object.values(this.draftClaimList))) {
        if (
          claim.claimNumber &&
          String(_get(claim, 'autoClaimDetail.vehicles[0].vin', '')) ===
            String(_get(this.draftClaim, 'autoClaimDetail.vehicles[0].vin', '')) &&
          String(_get(claim, 'lossDate', 'T')).split('T')[0] ===
            String(_get(draftClaimPayLoad, 'lossDate', 'T')).split('T')[0]
        ) {
          isDuplicate = true;
          this.store.dispatch(new fromClaimFnolActions.SaveDraftClaim(draftClaim));
          this.store.dispatch(
            fromRouterActions.Go({
              path: [
                ClaimsNavigationPaths.CLAIM_HOME_PATH +
                  '/report-claim-fnol/existing-claim/' +
                  claim.claimNumber
              ]
            })
          );
          break;
        }
      }
      if (!isDuplicate) {
        this.spinner.start({
          blockActions: true
        });
        this.store.dispatch(new fromClaimFnolActions.CreateDraftClaim(draftClaim));
      }
    }
  }
}
